/*!
 * Cover Dog - cart display and multi-copy pricing.
 *
 * 1. Cart display: shows the customer's own cover (the "_Cover Proof" photo saved with the line item)
 *    instead of the stock calendar image in the cart page and the cart drawer, and tidies the photo
 *    lines the drawer prints under the item name.
 * 2. Multiple copies: a "How many calendars?" picker in the Cover Dog photo box. The calendar is added
 *    as ONE line (its -C / -C12 SKU) with that quantity. Multi-copy pricing comes from discount codes
 *    COVERDOG-C-2 ... COVERDOG-C-25 and COVERDOG-C12-2 ... COVERDOG-C12-25 (Shopify > Discounts):
 *    first calendar full price, copies 2-5 $10 each, copies 6+ $7 each. This script applies the
 *    matching code to the cart automatically and keeps it in step when quantities change.
 */
(function () {
  'use strict';
  if (window.__coverDogCart) return;
  window.__coverDogCart = true;

  var PROOF = '_Cover Proof';
  var CODE_PREFIX = 'COVERDOG-';
  var PRICE_UP_TO_5 = 1000, PRICE_OVER_5 = 700; // cents; must match the discount codes
  var MAX_QTY = 25;                             // codes exist for 2-25 calendars

  function isUrl(v) { return typeof v === 'string' && /^(https?:)?\/\//.test(v); }
  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function split(total) {
    total = Math.max(1, total | 0);
    return { upTo5: Math.min(total - 1, 4), over5: Math.max(total - 5, 0) };
  }
  function totalCents(firstCents, total) {
    var s = split(total);
    return firstCents + s.upTo5 * PRICE_UP_TO_5 + s.over5 * PRICE_OVER_5;
  }
  function json(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) { return r.json(); });
  }
  function getCart() { return json('/cart.js'); }

  // Which personalized option a cart line is: 'C', 'C12' or null (SKU ends in -C / -C12).
  function optionOf(item) {
    var m = String(item.sku || '').match(/-(C12|C)$/);
    return m ? m[1] : null;
  }
  function codeFor(opt, qty) {
    if (!opt || qty < 2) return null;
    return CODE_PREFIX + opt + '-' + Math.min(qty, MAX_QTY);
  }
  function isOurCode(code) { return String(code || '').toUpperCase().indexOf(CODE_PREFIX) === 0; }

  /* ---------------- multi-copy code: keep the right one on the cart ---------------- */

  // Best code for this cart: per option, total calendars across its lines; if a cart has both
  // options with 2+ each, the bigger saving wins (codes don't combine with each other).
  function wantedCode(cart) {
    var qty = { C: 0, C12: 0 }, price = { C: 0, C12: 0 };
    (cart.items || []).forEach(function (it) {
      var o = optionOf(it);
      if (o) { qty[o] += it.quantity; price[o] = it.original_price; }
    });
    var best = null, bestSave = 0;
    ['C', 'C12'].forEach(function (o) {
      if (qty[o] < 2) return;
      var n = Math.min(qty[o], MAX_QTY);
      var save = price[o] * n - totalCents(price[o], n);
      if (save > bestSave) { bestSave = save; best = codeFor(o, qty[o]); }
    });
    return best;
  }

  var syncing = false;
  function syncCode(cart) {
    if (syncing) return Promise.resolve(false);
    var want = wantedCode(cart);
    var codes = (cart.discount_codes || []).map(function (d) { return d.code; });
    var ours = codes.filter(isOurCode);
    var theirs = codes.filter(function (c) { return !isOurCode(c); });
    // Never replace a code the customer typed in themselves.
    if (theirs.length) return Promise.resolve(false);
    if (want && ours.length === 1 && ours[0].toUpperCase() === want) return Promise.resolve(false);
    if (!want && !ours.length) return Promise.resolve(false);
    syncing = true;
    return json('/cart/update.js', { discount: want || '' })
      .then(function () { syncing = false; return true; }, function () { syncing = false; return false; });
  }

  /* ---------------- 1. cart display ---------------- */

  var running = false, again = false, timer = null;

  function apply() {
    if (running) { again = true; return; }
    var inCart = document.querySelector('.ajaxcart__row');
    running = true;
    getCart()
      .then(function (cart) {
        return syncCode(cart).then(function (changed) {
          if (!changed) return cart;
          // Cart page shows server-rendered totals: reload so the new discount shows.
          if (/^\/cart\/?$/.test(location.pathname)) { location.reload(); return null; }
          if (window.ajaxCart && window.ajaxCart.load) { try { window.ajaxCart.load(); } catch (e) {} }
          return getCart();
        });
      })
      .then(function (cart) {
        if (!cart || !inCart) return;
        var code = (cart.discount_codes || []).filter(function (d) { return isOurCode(d.code) && d.applicable; })[0];
        (cart.items || []).forEach(function (item, i) {
          var props = item.properties || {};
          var proof = props[PROOF];
          var rows = document.querySelectorAll('.ajaxcart__row[data-line="' + (i + 1) + '"]');
          Array.prototype.forEach.call(rows, function (row) {
            if (isUrl(proof)) {
              var img = row.querySelector('.cart-table__image img');
              if (img && img.getAttribute('src') !== proof) {
                img.removeAttribute('srcset');
                img.removeAttribute('data-srcset');
                img.setAttribute('data-src', proof);
                img.setAttribute('src', proof);
                img.alt = 'Your custom cover';
                img.classList.add('cdog-cart-img');
              }
            }
            // Hide internal (_) properties; show uploaded photos as a short "added" note.
            Array.prototype.forEach.call(row.querySelectorAll('.cart-table__details small'), function (el) {
              var text = el.textContent || '';
              var key = text.split(':')[0].trim();
              if (key.charAt(0) === '_') { el.style.display = 'none'; return; }
              if (/ Photo$/.test(key) && isUrl(props[key]) && !el.getAttribute('data-cdog')) {
                el.setAttribute('data-cdog', '1');
                el.textContent = key + ': ✓ added';
              }
            });
            // Multi-copy note on personalized lines.
            var note = row.querySelector('.cdog-cart-copies');
            var opt = optionOf(item);
            if (opt && item.quantity > 1 && code) {
              if (!note) {
                note = document.createElement('small');
                note.className = 'cdog-cart-copies';
                note.style.display = 'block';
                var details = row.querySelector('.cart-table__details') || row;
                details.appendChild(note);
              }
              note.textContent = 'Extra copies priced at $10 / $7: code ' + code.code + ' applied';
            } else if (note) {
              note.parentNode.removeChild(note);
            }
          });
        });
      })
      .catch(function () { /* cart display is cosmetic; never block the cart */ })
      .then(function () {
        running = false;
        if (again) { again = false; schedule(); }
      });
  }

  function schedule() { clearTimeout(timer); timer = setTimeout(apply, 150); }

  /* ---------------- 2. "How many calendars?" picker on the product page ---------------- */

  var pickerCount = 1;

  function cfgFor(widget) {
    var el = document.getElementById('CoverDogConfig-' + widget.getAttribute('data-cover-dog'));
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }

  function currentVariant(widget, cfg) {
    var checked = widget.querySelector('.cdog__tiers input[type=radio]:checked');
    var id = checked ? String(checked.value) : null;
    return cfg.variants.filter(function (v) { return String(v.id) === id; })[0] || null;
  }
  function optionOfValue(value) {
    var v = String(value || '').toLowerCase();
    if (/all\s*1[23]|all\s*images|12\s*months|every/.test(v)) return 'C12';
    if (/cover/.test(v)) return 'C';
    return null;
  }

  function injectStyles() {
    if (document.getElementById('cdog-copies-css')) return;
    var css =
      '.cdog-copies{display:grid;gap:8px;margin:14px 0 4px;padding:12px 14px;border:1px solid rgba(0,0,0,.15);border-radius:8px;background:rgba(0,0,0,.02)}' +
      '.cdog-copies__row{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}' +
      '.cdog-copies__label{font-weight:700;margin:0}' +
      '.cdog-copies__step{display:inline-flex;align-items:center;border:1px solid rgba(0,0,0,.25);border-radius:6px;overflow:hidden}' +
      '.cdog-copies__step button{width:38px;height:38px;border:0;background:transparent;font-size:20px;line-height:1;cursor:pointer;color:inherit}' +
      '.cdog-copies__step button:disabled{opacity:.35;cursor:default}' +
      '.cdog-copies__step input{width:52px;height:38px;border:0;border-left:1px solid rgba(0,0,0,.15);border-right:1px solid rgba(0,0,0,.15);text-align:center;font:inherit;font-weight:700;-moz-appearance:textfield;background:transparent;color:inherit}' +
      '.cdog-copies__step input::-webkit-outer-spin-button,.cdog-copies__step input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}' +
      '.cdog-copies__note{margin:0;font-size:.9em;opacity:.8}' +
      '.cdog-copies__total{margin:0;font-weight:700;font-variant-numeric:tabular-nums}' +
      '.cdog-copies__code{margin:0;font-size:.9em}' +
      '.cdog-copies__code b{font-family:ui-monospace,Menlo,Consolas,monospace;letter-spacing:.02em;padding:1px 6px;border:1px dashed rgba(0,0,0,.35);border-radius:4px}' +
      '.cdog-personalized .cdog-qty-hidden,.cdog-personalized form[action*="/cart/add"] .product-form__item--quantity{display:none!important}';
    var s = document.createElement('style');
    s.id = 'cdog-copies-css';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function buildPicker(widget) {
    if (widget.__cdogCopies) return;
    var work = widget.querySelector('.cdog__work');
    var cfg = cfgFor(widget);
    if (!work || !cfg) return;
    widget.__cdogCopies = true;
    injectStyles();

    var box = document.createElement('div');
    box.className = 'cdog-copies';
    box.innerHTML =
      '<div class="cdog-copies__row">' +
        '<label class="cdog-copies__label" for="cdog-copies-' + cfg.productId + '">How many calendars?</label>' +
        '<span class="cdog-copies__step">' +
          '<button type="button" data-d="-1" aria-label="One fewer">&minus;</button>' +
          // type="text": the theme turns every number field into its own quantity widget.
          '<input id="cdog-copies-' + cfg.productId + '" class="cdog-copies__num" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" value="1">' +
          '<button type="button" data-d="1" aria-label="One more">+</button>' +
        '</span>' +
      '</div>' +
      '<p class="cdog-copies__note">Extra copies of the same calendar: $10 each for copies 2–5, then $7 each. Need more than ' + MAX_QTY + '? Call us at 407-422-4700.</p>' +
      '<p class="cdog-copies__total" aria-live="polite"></p>' +
      '<p class="cdog-copies__code" hidden></p>';

    var buy = work.querySelector('[data-cdog-buy-now]');
    work.insertBefore(box, buy || null);

    var input = box.querySelector('input');
    var minus = box.querySelector('[data-d="-1"]');
    var plus = box.querySelector('[data-d="1"]');
    var totalEl = box.querySelector('.cdog-copies__total');
    var codeEl = box.querySelector('.cdog-copies__code');

    function render() {
      input.value = pickerCount;
      minus.disabled = pickerCount <= 1;
      plus.disabled = pickerCount >= MAX_QTY;
      var v = currentVariant(widget, cfg);
      var code = v && codeFor(optionOfValue(v.value), pickerCount);
      codeEl.hidden = !code;
      if (code) codeEl.innerHTML = 'Multi-copy code <b>' + code + '</b> is applied for you automatically.';
      if (!v) { totalEl.textContent = ''; return; }
      if (pickerCount === 1) { totalEl.textContent = '1 calendar: ' + money(v.price); return; }
      var s = split(pickerCount);
      var parts = [money(v.price) + ' first calendar'];
      if (s.upTo5) parts.push(s.upTo5 + ' × $10');
      if (s.over5) parts.push(s.over5 + ' × $7');
      totalEl.textContent = pickerCount + ' calendars: ' + money(totalCents(v.price, pickerCount)) +
        ' (' + parts.join(' + ') + ')';
    }
    function set(n) {
      n = parseInt(n, 10);
      pickerCount = isNaN(n) ? 1 : Math.min(MAX_QTY, Math.max(1, n));
      render();
    }
    minus.addEventListener('click', function () { set(pickerCount - 1); });
    plus.addEventListener('click', function () { set(pickerCount + 1); });
    input.addEventListener('change', function () { set(input.value); });
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); set(input.value); } });
    widget.querySelector('.cdog__tiers').addEventListener('change', function () { setTimeout(render, 0); });

    // The theme's own quantity box is replaced by the picker while a personalized option is selected.
    document.querySelectorAll('form[action*="/cart/add"] input[name="quantity"]').forEach(function (q) {
      var wrap = q.closest('.product-form__item, .product-form__quantity, .js-qty, .quantity, .qty-selector') || q;
      wrap.classList.add('cdog-qty-hidden');
      var lbl = q.id && document.querySelector('label[for="' + q.id + '"]');
      if (lbl) lbl.classList.add('cdog-qty-hidden');
    });

    render();
  }

  function findWidgets() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cover-dog]'), function (w) {
      if (!w.hidden) buildPicker(w);
    });
  }

  /* ---------------- 2b. add the calendar with the chosen quantity, then apply the code ---------------- */

  // cover-dog.js uploads the photos with fetch('/cart/add', FormData). When that request carries a
  // Cover Dog proof, set the quantity from the picker and put the matching code on the cart before
  // the page moves on to the cart or checkout.
  if (window.fetch && !window.fetch.__cdogCopies) {
    var origFetch = window.fetch;
    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var body = init && init.body;
      var isCoverDogAdd = /\/cart\/add(\?|$)/.test(url) && init && /post/i.test(init.method || '') &&
        typeof FormData !== 'undefined' && body instanceof FormData && body.has('properties[' + PROOF + ']');
      if (!isCoverDogAdd) return origFetch.apply(this, arguments);

      body.set('quantity', String(pickerCount));
      return origFetch.apply(this, arguments).then(function (res) {
        if (!res.ok) return res;
        return getCart().then(syncCode).then(function () { return res; }, function () { return res; });
      });
    };
    wrapped.__cdogCopies = true;
    window.fetch = wrapped;
  }

  /* ---------------- boot ---------------- */

  function boot() { schedule(); findWidgets(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('load', findWidgets);

  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i], n = m.target;
        if (m.type === 'attributes') { if (n.hasAttribute && n.hasAttribute('data-cover-dog')) findWidgets(); continue; }
        if (n.nodeType === 1 && (n.closest('.CartContainer') || n.closest('.cart-table')) && m.addedNodes.length) {
          schedule();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
  }

  // Pure helpers, exposed for testing.
  window.CoverDogCopies = { split: split, totalCents: totalCents, codeFor: codeFor, wantedCode: wantedCode };
})();
